import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import './UserAuth.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const EMERGENT_AUTH_URL = 'https://auth.emergentagent.com';

const UserRegister = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    name: ''
  });
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Check for session_id in URL fragment (from Emergent Auth)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      handleEmergentAuth(sessionId);
    }
  }, []);

  const handleEmergentAuth = async (sessionId) => {
    setLoading(true);
    try {
      // Get session data from Emergent
      const response = await axios.get('https://demobackend.emergentagent.com/auth/v1/env/oauth/session-data', {
        headers: {
          'X-Session-ID': sessionId
        }
      });

      const { id, email, name, picture, session_token } = response.data;

      // Send to our backend to create/update user
      const userResponse = await axios.post(`${API}/auth/google-login`, {
        google_id: id,
        email: email,
        name: name,
        picture: picture,
        session_token: session_token
      });

      // Store session
      localStorage.setItem('user_session_token', userResponse.data.session_token);
      localStorage.setItem('user', JSON.stringify(userResponse.data.user));

      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);

      toast.success(`Willkommen, ${name}!`);
      navigate('/setup');

    } catch (error) {
      console.error('Google auth error:', error);
      toast.error('Google Registrierung fehlgeschlagen');
      setLoading(false);
    }
  };

  const handleGoogleRegister = () => {
    const redirectUrl = `${window.location.origin}/register`;
    window.location.href = `${EMERGENT_AUTH_URL}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.username.trim() || !formData.email.trim() || !formData.password.trim()) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    if (!acceptedPrivacy) {
      toast.error('Bitte akzeptiere die Datenschutzerklärung');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        username: formData.username.trim(),
        email: formData.email.trim(),
        password: formData.password.trim(),
        name: formData.name.trim() || formData.username.trim()
      });

      const { user, session_token } = response.data;

      // Store session
      localStorage.setItem('user_session_token', session_token);
      localStorage.setItem('user', JSON.stringify(user));

      toast.success(`Willkommen, ${user.name}! Account erstellt.`);
      navigate('/setup');

    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registrierung fehlgeschlagen');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-container">
        <div className="auth-loading">
          <div className="loading-spinner"></div>
          <p>Registrierung läuft...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-content">
        <Button 
          variant="ghost" 
          className="back-button"
          onClick={() => navigate('/choose-mode')}
        >
          <ArrowLeft size={20} /> Zurück
        </Button>

        <Card className="auth-card">
          <CardHeader>
            <CardTitle>Account erstellen</CardTitle>
            <CardDescription>Registriere dich kostenlos</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Google Register */}
            <Button
              onClick={handleGoogleRegister}
              className="google-button"
              variant="outline"
              disabled={loading}
            >
              <img src="https://www.google.com/favicon.ico" alt="Google" className="google-icon" />
              Mit Google registrieren
            </Button>

            <div className="divider">
              <span>oder</span>
            </div>

            {/* Email/Password Register */}
            <form onSubmit={handleEmailRegister}>
              <div className="form-group">
                <Label htmlFor="username">Benutzername *</Label>
                <Input
                  id="username"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="deinusername"
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <Label htmlFor="name">Name (optional)</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Dein Name"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="deine@email.com"
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <Label htmlFor="password">Passwort *</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Mindestens 8 Zeichen"
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <Label htmlFor="confirmPassword">Passwort bestätigen *</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Passwort wiederholen"
                  disabled={loading}
                  required
                />
              </div>

              <div className="checkbox-group">
                <Checkbox
                  id="privacy"
                  checked={acceptedPrivacy}
                  onCheckedChange={setAcceptedPrivacy}
                  disabled={loading}
                />
                <Label htmlFor="privacy" className="checkbox-label">
                  Ich habe die <a href="/privacy-policy" target="_blank">Datenschutzerklärung</a> gelesen 
                  und akzeptiere die <a href="/terms" target="_blank">Nutzungsbedingungen</a>
                </Label>
              </div>

              <Button 
                type="submit" 
                className="submit-button"
                disabled={loading || !acceptedPrivacy}
              >
                {loading ? 'Registrierung...' : 'Kostenlos registrieren'}
              </Button>
            </form>

            <div className="auth-footer">
              <p>
                Bereits registriert? 
                <a href="/login"> Jetzt anmelden</a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserRegister;
